import { useRef, useState } from 'react';
import { createJob, uploadToS3 } from '../api';

interface Props {
  token: string;
  onUploaded: () => void | Promise<void>;
}

interface UploadState {
  fileName: string;
  percent: number;
}

const ACCEPT = '.mp3,.m4a,.wav,.flac,.ogg,.webm,.mp4,audio/*';

export function UploadPanel({ token, onUploaded }: Props) {
  const [upload, setUpload] = useState<UploadState>();
  const [error, setError] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    setError(undefined);
    setUpload({ fileName: file.name, percent: 0 });
    try {
      const { uploadUrl, contentType } = await createJob(token, file);
      await uploadToS3(uploadUrl, file, contentType, (percent) =>
        setUpload({ fileName: file.name, percent }),
      );
      // The S3 event kicks off the pipeline; the list picks it up on the next poll.
      await onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUpload(undefined);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <section
      className={`upload ${dragging ? 'dragging' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        void handleFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(event) => void handleFiles(event.target.files)}
      />

      {upload ? (
        <>
          <p>
            <strong>{upload.fileName}</strong> wird hochgeladen … {upload.percent}%
          </p>
          <div className="progress">
            <div className="progress-bar" style={{ width: `${upload.percent}%` }} />
          </div>
        </>
      ) : (
        <>
          <p className="upload-hint">Audiodatei hierher ziehen</p>
          <button className="primary" onClick={() => inputRef.current?.click()}>
            Datei auswählen
          </button>
          <p className="muted small">MP3, M4A, WAV, FLAC, OGG · bis 2 GB / 4 Stunden</p>
        </>
      )}

      {error && <p className="error">{error}</p>}
    </section>
  );
}
